import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Input, Select } from '@components/Input';
import { Card, CardContent, CardHeader } from '@components/Card';
import { freightHubConfigService } from '@/services/freightHubConfigService';
import { companyService } from '@/services/companyService';
import { formatDateBR, formatDateTimeBR } from '@/pages/financial/financialUtils';
import { Company } from '@/types/company';
import {
  FREIGHT_HUB_LABEL_ROLES,
  FREIGHT_HUB_PROVIDER_KEYS,
  FREIGHT_HUB_QUOTE_BUDGET_MAX_SECONDS,
  FreightHubLabelRole,
  FreightHubPackingReport,
  FreightHubProviderKey,
} from '@/types/freightHubConfig';
import {
  BoxFormRow,
  FreightHubFormState,
  LABEL_ROLE_LABELS,
  LabelFormRow,
  PROVIDER_LABELS,
  boxVolumeLiters,
  labelProviderName,
  pivotPackingWeeks,
  previewServiceLabel,
  summarizePackingWeeks,
  RangeFormRow,
  RestrictionFormRow,
  brlFromCents,
  buildFreightHubPayload,
  formFromDocument,
  simulateOwnFleet,
} from './freightHubConfig';

// Aba "Hub de Fretes" (Configuração → Transporte → Transportadoras): configuração do
// motor de cotação (FreightHub). Tudo o que antes vivia em variáveis de ambiente do
// FreightHub e no ProductsAPI. Salvar grava o documento inteiro e pede ao hub para
// recarregar; as demais réplicas aplicam no próximo ciclo (Recarga da configuração).

interface Props {
  canWrite: boolean;
}

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode; className?: string }> = ({ label, hint, children, className }) => (
  <label className={`block ${className ?? ''}`}>
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

const Check: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean; title?: string }> = ({ checked, onChange, label, disabled, title }) => (
  <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 whitespace-nowrap" title={title}>
    <input
      type="checkbox"
      className="h-3.5 w-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    {label}
  </label>
);

let newRowKey = -1;

const intBR = (n: number) => n.toLocaleString('pt-BR');
const pctBR = (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const STRATEGY_LABELS: Record<string, string> = { legacy: 'legacy (atual)', catalog: 'catalog (caixas)' };

export const FreightHubConfigPanel: React.FC<Props> = ({ canWrite }) => {
  const [form, setForm] = useState<FreightHubFormState | null>(null);
  const [meta, setMeta] = useState<{ updated_at?: string; updated_by?: string | null } | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'warn'; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sim, setSim] = useState({ zip: '', packages: '1', value: '' });
  const [report, setReport] = useState<FreightHubPackingReport | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const loadReport = useCallback(async () => {
    setReportLoading(true);
    setReportError(null);
    try {
      setReport(await freightHubConfigService.getPackingReport(8, 30));
    } catch (err) {
      setReportError(err instanceof Error ? err.message : 'Erro ao carregar a acurácia do empacotamento');
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const packingSummary = useMemo(() => summarizePackingWeeks(report?.weeks ?? []), [report]);
  const packingWeeks = useMemo(() => pivotPackingWeeks(report?.weeks ?? []), [report]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [doc, comps] = await Promise.all([freightHubConfigService.get(), companyService.getCompanies()]);
      setForm(formFromDocument(doc));
      setMeta({ updated_at: doc.config.updated_at, updated_by: doc.config.updated_by });
      setCompanies(comps);
      setDirty(false);
      setErrors([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar a configuração do Hub de Fretes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (fn: (prev: FreightHubFormState) => FreightHubFormState) => {
    setForm((prev) => (prev ? fn(prev) : prev));
    setDirty(true);
    setNotice(null);
  };
  const setField = <K extends keyof FreightHubFormState>(key: K, value: FreightHubFormState[K]) => update((p) => ({ ...p, [key]: value }));
  const setProvider = (key: FreightHubProviderKey, patch: Partial<FreightHubFormState['providers'][number]>) =>
    update((p) => ({ ...p, providers: p.providers.map((r) => (r.provider_key === key ? { ...r, ...patch } : r)) }));
  const setRestriction = (rowKey: number, patch: Partial<RestrictionFormRow>) =>
    update((p) => ({ ...p, zip_restrictions: p.zip_restrictions.map((r) => (r.key === rowKey ? { ...r, ...patch } : r)) }));
  const setRange = (rowKey: number, patch: Partial<RangeFormRow>) =>
    update((p) => ({ ...p, own_fleet_ranges: p.own_fleet_ranges.map((r) => (r.key === rowKey ? { ...r, ...patch } : r)) }));
  const setFleet = (patch: Partial<FreightHubFormState['own_fleet']>) => update((p) => ({ ...p, own_fleet: { ...p.own_fleet, ...patch } }));
  const setBox = (rowKey: number, patch: Partial<BoxFormRow>) =>
    update((p) => ({ ...p, boxes: p.boxes.map((r) => (r.key === rowKey ? { ...r, ...patch } : r)) }));
  const setLabel = (rowKey: number, patch: Partial<LabelFormRow>) =>
    update((p) => ({ ...p, service_labels: p.service_labels.map((r) => (r.key === rowKey ? { ...r, ...patch } : r)) }));

  const save = async () => {
    if (!form) return;
    const { payload, errors: validation } = buildFreightHubPayload(form);
    setErrors(validation);
    if (validation.length > 0) return;
    setSaving(true);
    setError(null);
    try {
      const result = await freightHubConfigService.save(payload);
      setForm(formFromDocument(result.document));
      setMeta({ updated_at: result.document.config.updated_at, updated_by: result.document.config.updated_by });
      setDirty(false);
      setNotice({ kind: result.hub_reloaded ? 'ok' : 'warn', text: result.hub_message });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const reloadHub = async () => {
    setReloading(true);
    setError(null);
    try {
      const res = await freightHubConfigService.reload();
      setNotice({
        kind: 'ok',
        text: `${res.message} (origem: ${res.source}${res.loaded_at ? `, carregada em ${formatDateTimeBR(res.loaded_at)}` : ''})`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao recarregar o hub');
    } finally {
      setReloading(false);
    }
  };

  const simulation = useMemo(() => {
    if (!form || !sim.zip.trim()) return null;
    const value = sim.value.trim() ? Math.round(Number(sim.value.replace(/\./g, '').replace(',', '.')) * 100) : 0;
    const packages = Number(sim.packages) || 1;
    const cents = simulateOwnFleet(form, sim.zip, packages, Number.isFinite(value) ? value : 0);
    return cents == null ? 'fora das faixas atendidas' : `R$ ${brlFromCents(cents)}`;
  }, [form, sim]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-6">
        <RefreshCw size={16} className="animate-spin" /> Carregando configuração do hub...
      </div>
    );
  }
  if (!form) {
    return (
      <div className="flex items-center gap-2 text-red-600 text-sm py-6">
        <AlertCircle size={16} /> {error ?? 'Configuração indisponível'}
        <Button variant="outline" size="sm" onClick={load}>Tentar de novo</Button>
      </div>
    );
  }

  const ro = !canWrite;

  return (
    <div className="space-y-5">
      {/* Barra de ações */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-500">
          {meta?.updated_at && (
            <>Última alteração {formatDateTimeBR(meta.updated_at)}{meta.updated_by ? ` por ${meta.updated_by}` : ''}. </>
          )}
          Os pods do hub aplicam a configuração em até {form.config_refresh_seconds || '60'} s; salvar já aplica em uma réplica.
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={reloadHub} loading={reloading} icon={<RefreshCw size={14} />} title="Pede ao FreightHub para reler a configuração agora">
              Recarregar hub agora
            </Button>
            <Button variant="primary" size="sm" onClick={save} loading={saving} disabled={!dirty} icon={<Save size={14} />}>
              Salvar
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {errors.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <div className="font-medium mb-1">Corrija antes de salvar:</div>
          <ul className="list-disc pl-5 space-y-0.5">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {notice && (
        <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${notice.kind === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
          {notice.kind === 'ok' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
          {notice.text}
        </div>
      )}

      {/* Precificação */}
      <Card>
        <CardHeader title="Precificação (preço ao cliente no site)" subtitle="O ERP recebe o custo da transportadora + a sobretaxa; o site recebe custo mínimo, markup e sobretaxa conforme a política de cada transportadora abaixo." />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Field label="Markup (%)" hint="Imposto/margem sobre o frete. Ex.: 10,30">
              <Input value={form.markup_percent} disabled={ro} onChange={(e) => setField('markup_percent', e.target.value)} />
            </Field>
            <Field label="Custo mínimo (R$)" hint="Piso antes do markup">
              <Input value={form.min_shipping_cost} disabled={ro} onChange={(e) => setField('min_shipping_cost', e.target.value)} />
            </Field>
            <Field label="Teto do valor declarado (R$)" hint="Vazio = sem teto">
              <Input value={form.declared_value_cap} disabled={ro} onChange={(e) => setField('declared_value_cap', e.target.value)} />
            </Field>
            <Field label="Sobretaxa por R$ 1.000 acima do teto (R$)" hint="Seguro além do teto (ERP e site)">
              <Input value={form.excess_surcharge_per_1000} disabled={ro} onChange={(e) => setField('excess_surcharge_per_1000', e.target.value)} />
            </Field>
            <Field label="Dias de embalagem" hint="Somados ao prazo exibido no site">
              <Input value={form.packing_days} disabled={ro} onChange={(e) => setField('packing_days', e.target.value)} />
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Desempenho e origem */}
      <Card>
        <CardHeader title="Desempenho, origem e empresa padrão" />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Timeout por transportadora (s)" hint="1 a 120">
              <Input value={form.provider_timeout_seconds} disabled={ro} onChange={(e) => setField('provider_timeout_seconds', e.target.value)} />
            </Field>
            <Field label="Orçamento total da cotação (s)" hint={`Máx. ${FREIGHT_HUB_QUOTE_BUDGET_MAX_SECONDS} (o site espera 15 s); ao estourar, responde com o que chegou`}>
              <Input value={form.quote_budget_seconds} disabled={ro} onChange={(e) => setField('quote_budget_seconds', e.target.value)} />
            </Field>
            <Field label="Cache de cotação (min)" hint="Por CEP + itens (site)">
              <Input value={form.quote_cache_minutes} disabled={ro} onChange={(e) => setField('quote_cache_minutes', e.target.value)} />
            </Field>
            <Field label="Recarga da configuração (s)" hint="Intervalo em que os pods releem esta tela">
              <Input value={form.config_refresh_seconds} disabled={ro} onChange={(e) => setField('config_refresh_seconds', e.target.value)} />
            </Field>
            <Field label="Recarga do catálogo (s)" hint="Produtos/kits para a cotação por SKU">
              <Input value={form.catalog_refresh_seconds} disabled={ro} onChange={(e) => setField('catalog_refresh_seconds', e.target.value)} />
            </Field>
            <Field label="Falhas para abrir o circuito" hint="Falhas seguidas de uma transportadora">
              <Input value={form.breaker_failures} disabled={ro} onChange={(e) => setField('breaker_failures', e.target.value)} />
            </Field>
            <Field label="Circuito aberto por (s)" hint="Depois disso, uma sondagem">
              <Input value={form.breaker_open_seconds} disabled={ro} onChange={(e) => setField('breaker_open_seconds', e.target.value)} />
            </Field>
            <Field label="CEP de origem" hint="Quando a cotação não informa">
              <Input value={form.origin_zip} disabled={ro} onChange={(e) => setField('origin_zip', e.target.value)} />
            </Field>
            <Field label="Empresa padrão" hint="Contrato Correios / CNPJ na FM quando a cotação não traz empresa (site)">
              <Select value={form.default_company_id} disabled={ro} onChange={(e) => setField('default_company_id', e.target.value)}>
                {companies.map((c) => (
                  <option key={c.company_id} value={String(c.company_id)}>{c.company_id} — {c.name}</option>
                ))}
                {!companies.some((c) => String(c.company_id) === form.default_company_id) && (
                  <option value={form.default_company_id}>{form.default_company_id}</option>
                )}
              </Select>
            </Field>
          </div>
        </CardContent>
      </Card>

      {/* Transportadoras */}
      <Card>
        <CardHeader title="Transportadoras no motor" subtitle="Canal ERP = pedidos e convites; canal site = loja e propostas do CRM. A política de preço vale só no site." />
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-1.5">Transportadora</th>
                  <th className="px-2 py-1.5">Nome exibido</th>
                  <th className="px-2 py-1.5">Canais</th>
                  <th className="px-2 py-1.5">Timeout (s)</th>
                  <th className="px-2 py-1.5">URL da API</th>
                  <th className="px-2 py-1.5">Cache (min)</th>
                  <th className="px-2 py-1.5">Política no site</th>
                </tr>
              </thead>
              <tbody>
                {form.providers.map((p) => (
                  <tr key={p.provider_key} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">
                      {PROVIDER_LABELS[p.provider_key]}
                      <div className="text-[11px] font-normal text-slate-400">{p.provider_key}</div>
                    </td>
                    <td className="px-2 py-2 min-w-[10rem]"><Input value={p.display_name} disabled={ro} onChange={(e) => setProvider(p.provider_key, { display_name: e.target.value })} /></td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1">
                        <Check checked={p.enabled} disabled={ro} onChange={(v) => setProvider(p.provider_key, { enabled: v })} label="Ativa" />
                        <Check checked={p.enabled_erp} disabled={ro} onChange={(v) => setProvider(p.provider_key, { enabled_erp: v })} label="ERP" />
                        <Check checked={p.enabled_site} disabled={ro} onChange={(v) => setProvider(p.provider_key, { enabled_site: v })} label="Site" />
                      </div>
                    </td>
                    <td className="px-2 py-2 w-24"><Input value={p.timeout_seconds} placeholder="global" disabled={ro} onChange={(e) => setProvider(p.provider_key, { timeout_seconds: e.target.value })} /></td>
                    <td className="px-2 py-2 min-w-[14rem]">
                      <Input value={p.endpoint_url} placeholder={p.provider_key === 'fmtransportes' ? 'https://…/api/v1/quote' : 'padrão da integração'} disabled={ro || p.provider_key === 'viverorganizer'} onChange={(e) => setProvider(p.provider_key, { endpoint_url: e.target.value })} />
                    </td>
                    <td className="px-2 py-2 w-24"><Input value={p.quote_cache_minutes} placeholder="—" disabled={ro} onChange={(e) => setProvider(p.provider_key, { quote_cache_minutes: e.target.value })} /></td>
                    <td className="px-2 py-2">
                      <div className="flex flex-col gap-1">
                        <Check checked={p.apply_min_shipping_cost} disabled={ro} onChange={(v) => setProvider(p.provider_key, { apply_min_shipping_cost: v })} label="Custo mínimo" />
                        <Check checked={p.apply_markup} disabled={ro} onChange={(v) => setProvider(p.provider_key, { apply_markup: v })} label="Markup" />
                        <Check checked={p.apply_surcharge} disabled={ro} onChange={(v) => setProvider(p.provider_key, { apply_surcharge: v })} label="Sobretaxa acima do teto" />
                        <Check checked={p.add_packing_days} disabled={ro} onChange={(v) => setProvider(p.provider_key, { add_packing_days: v })} label="Soma dias de embalagem" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Restrições de CEP */}
      <Card>
        <CardHeader
          title="Restrições de CEP por transportadora"
          subtitle="Faixas que a transportadora não atende: o hub nem consulta a API. Várias por transportadora; sobreposição permitida."
          action={canWrite ? (
            <Button variant="outline" size="sm" icon={<Plus size={14} />} onClick={() => update((p) => ({
              ...p,
              zip_restrictions: [...p.zip_restrictions, { key: newRowKey--, provider_key: 'fmtransportes', zip_from: '', zip_to: '', reason: '', enabled: true }],
            }))}>
              Nova faixa
            </Button>
          ) : undefined}
        />
        <CardContent>
          {form.zip_restrictions.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma restrição cadastrada.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-1.5">Transportadora</th>
                    <th className="px-2 py-1.5">CEP de</th>
                    <th className="px-2 py-1.5">CEP até</th>
                    <th className="px-2 py-1.5">Motivo</th>
                    <th className="px-2 py-1.5">Ativa</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {form.zip_restrictions.map((r) => (
                    <tr key={r.key} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 w-52">
                        <Select value={r.provider_key} disabled={ro} onChange={(e) => setRestriction(r.key, { provider_key: e.target.value as FreightHubProviderKey })}>
                          {FREIGHT_HUB_PROVIDER_KEYS.map((k) => <option key={k} value={k}>{PROVIDER_LABELS[k]}</option>)}
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 w-32"><Input value={r.zip_from} placeholder="13000-000" disabled={ro} onChange={(e) => setRestriction(r.key, { zip_from: e.target.value })} /></td>
                      <td className="px-2 py-1.5 w-32"><Input value={r.zip_to} placeholder="13499-999" disabled={ro} onChange={(e) => setRestriction(r.key, { zip_to: e.target.value })} /></td>
                      <td className="px-2 py-1.5"><Input value={r.reason} placeholder="Ex.: Jundiaí e região" disabled={ro} onChange={(e) => setRestriction(r.key, { reason: e.target.value })} /></td>
                      <td className="px-2 py-1.5"><Check checked={r.enabled} disabled={ro} onChange={(v) => setRestriction(r.key, { enabled: v })} label="" /></td>
                      <td className="px-2 py-1.5 w-10">
                        {canWrite && (
                          <button type="button" className="text-slate-400 hover:text-red-600" title="Remover" onClick={() => update((p) => ({ ...p, zip_restrictions: p.zip_restrictions.filter((x) => x.key !== r.key) }))}>
                            <Trash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Frota própria */}
      <Card>
        <CardHeader
          title="Frota própria (Viver Organizer)"
          subtitle="Preço = base da faixa do CEP + volumes acima dos inclusos × taxa + % sobre a mercadoria. Faixas ativas não podem se sobrepor."
          action={canWrite ? (
            <Button variant="outline" size="sm" icon={<Plus size={14} />} onClick={() => update((p) => ({
              ...p,
              own_fleet_ranges: [...p.own_fleet_ranges, { key: newRowKey--, zip_prefix_from: '', zip_prefix_to: '', price: '', label: '', enabled: true }],
            }))}>
              Nova faixa
            </Button>
          ) : undefined}
        />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Field label="Nome do serviço" hint="Aparece na cotação do ERP">
              <Input value={form.own_fleet.service_name} disabled={ro} onChange={(e) => setFleet({ service_name: e.target.value })} />
            </Field>
            <Field label="Prazo (dias úteis)">
              <Input value={form.own_fleet.delivery_days} disabled={ro} onChange={(e) => setFleet({ delivery_days: e.target.value })} />
            </Field>
            <Field label="Volumes inclusos no preço base">
              <Input value={form.own_fleet.included_packages} disabled={ro} onChange={(e) => setFleet({ included_packages: e.target.value })} />
            </Field>
            <Field label="Taxa por volume adicional (R$)">
              <Input value={form.own_fleet.additional_package_fee} disabled={ro} onChange={(e) => setFleet({ additional_package_fee: e.target.value })} />
            </Field>
            <Field label="% sobre o valor da mercadoria" hint="Ex.: 1,00">
              <Input value={form.own_fleet.percent_fee} disabled={ro} onChange={(e) => setFleet({ percent_fee: e.target.value })} />
            </Field>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-1.5">Prefixo de (5 dígitos)</th>
                  <th className="px-2 py-1.5">Prefixo até</th>
                  <th className="px-2 py-1.5">Preço base (R$)</th>
                  <th className="px-2 py-1.5">Rótulo</th>
                  <th className="px-2 py-1.5">Ativa</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {form.own_fleet_ranges.map((b) => (
                  <tr key={b.key} className="border-t border-slate-100">
                    <td className="px-2 py-1.5 w-36"><Input value={b.zip_prefix_from} placeholder="01000" disabled={ro} onChange={(e) => setRange(b.key, { zip_prefix_from: e.target.value })} /></td>
                    <td className="px-2 py-1.5 w-36"><Input value={b.zip_prefix_to} placeholder="05999" disabled={ro} onChange={(e) => setRange(b.key, { zip_prefix_to: e.target.value })} /></td>
                    <td className="px-2 py-1.5 w-32"><Input value={b.price} placeholder="62,00" disabled={ro} onChange={(e) => setRange(b.key, { price: e.target.value })} /></td>
                    <td className="px-2 py-1.5"><Input value={b.label} placeholder="Ex.: São Paulo capital" disabled={ro} onChange={(e) => setRange(b.key, { label: e.target.value })} /></td>
                    <td className="px-2 py-1.5"><Check checked={b.enabled} disabled={ro} onChange={(v) => setRange(b.key, { enabled: v })} label="" /></td>
                    <td className="px-2 py-1.5 w-10">
                      {canWrite && (
                        <button type="button" className="text-slate-400 hover:text-red-600" title="Remover" onClick={() => update((p) => ({ ...p, own_fleet_ranges: p.own_fleet_ranges.filter((x) => x.key !== b.key) }))}>
                          <Trash2 size={15} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Simulador */}
          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-600 mb-2">Simular frota própria (com os valores desta tela, antes de salvar)</div>
            <div className="flex flex-wrap items-end gap-3">
              <Field label="CEP de destino"><Input value={sim.zip} placeholder="01310-100" onChange={(e) => setSim((s) => ({ ...s, zip: e.target.value }))} /></Field>
              <Field label="Volumes"><Input value={sim.packages} onChange={(e) => setSim((s) => ({ ...s, packages: e.target.value }))} /></Field>
              <Field label="Valor da mercadoria (R$)"><Input value={sim.value} placeholder="1.000,00" onChange={(e) => setSim((s) => ({ ...s, value: e.target.value }))} /></Field>
              <div className="text-sm text-slate-800 pb-2">
                {simulation ? <>Custo da frota: <span className="font-semibold">{simulation}</span></> : <span className="text-slate-400">informe um CEP</span>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Empacotamento */}
      <Card>
        <CardHeader
          title="Empacotamento"
          subtitle="Como o hub transforma os itens do carrinho em volumes. legacy = cubos do ProductsAPI (o preço de hoje); catalog = caixas do catálogo + volume efetivo aprendido por SKU, nunca com mais caixas que o legacy."
        />
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Field label="Estratégia" hint="Troque para catalog depois de acompanhar a acurácia abaixo">
              <Select value={form.packing_strategy} disabled={ro} onChange={(e) => setField('packing_strategy', e.target.value as 'legacy' | 'catalog')}>
                <option value="legacy">legacy (cubos, como o site hoje)</option>
                <option value="catalog">catalog (catálogo de caixas)</option>
              </Select>
            </Field>
            <Field label="Ocupação alvo da caixa (%)" hint="10 a 100. Margem para acomodar os itens">
              <Input value={form.pack_fill_target} disabled={ro} onChange={(e) => setField('pack_fill_target', e.target.value)} />
            </Field>
            <Field label="Amostras mínimas por SKU" hint="Abaixo disso o SKU usa o fator da família">
              <Input value={form.pack_min_samples} disabled={ro} onChange={(e) => setField('pack_min_samples', e.target.value)} />
            </Field>
            <Field label="Fator: item plano empilhável (%)" hint="Cabides. % do volume com folga">
              <Input value={form.pack_factor_flat_stack} disabled={ro} onChange={(e) => setField('pack_factor_flat_stack', e.target.value)} />
            </Field>
            <Field label="Fator: item plano rígido (%)" hint="Capas, placas de acrílico">
              <Input value={form.pack_factor_flat_upright} disabled={ro} onChange={(e) => setField('pack_factor_flat_upright', e.target.value)} />
            </Field>
            <Field label="Fator: item volumétrico (%)" hint="Caixas organizadoras e demais">
              <Input value={form.pack_factor_rigid} disabled={ro} onChange={(e) => setField('pack_factor_rigid', e.target.value)} />
            </Field>
          </div>
          <div className="mt-4">
            <Check
              checked={form.legacy_fastest_rule}
              disabled={ro}
              onChange={(v) => setField('legacy_fastest_rule', v)}
              label="Manter a regra antiga da opção “mais rápida”"
              title="O ProductsAPI compara o prazo exibido (com dias de embalagem) com o prazo bruto da outra opção"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Ligado = mesmo resultado do ProductsAPI, que às vezes omite a opção Expresso. Desligue junto com a troca para catalog e atualize a baseline da regressão.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Catálogo de caixas */}
      <Card>
        <CardHeader
          title="Catálogo de caixas"
          subtitle="Medidas internas das caixas da expedição. A estratégia catalog escolhe a menor caixa que comporta os itens; peso do volume = produtos + tara."
          action={canWrite ? (
            <Button variant="outline" size="sm" icon={<Plus size={14} />} onClick={() => update((p) => ({
              ...p,
              boxes: [...p.boxes, { key: newRowKey--, name: '', inner_height: '', inner_width: '', inner_length: '', tare: '0,3', max_weight: '15', priority: String((p.boxes.length + 1) * 10), enabled: true }],
            }))}>
              Nova caixa
            </Button>
          ) : undefined}
        />
        <CardContent>
          {form.boxes.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhuma caixa cadastrada. Sem caixas, o hub usa o empacotamento legacy.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-1.5">Nome</th>
                    <th className="px-2 py-1.5">Altura (cm)</th>
                    <th className="px-2 py-1.5">Largura (cm)</th>
                    <th className="px-2 py-1.5">Compr. (cm)</th>
                    <th className="px-2 py-1.5 text-right">Volume</th>
                    <th className="px-2 py-1.5">Tara (kg)</th>
                    <th className="px-2 py-1.5">Peso máx. (kg)</th>
                    <th className="px-2 py-1.5">Prioridade</th>
                    <th className="px-2 py-1.5">Ativa</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {form.boxes.map((b) => {
                    const liters = boxVolumeLiters(b);
                    return (
                      <tr key={b.key} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 min-w-[11rem]"><Input value={b.name} placeholder="Caixa 51x31x26" disabled={ro} onChange={(e) => setBox(b.key, { name: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.inner_height} disabled={ro} onChange={(e) => setBox(b.key, { inner_height: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.inner_width} disabled={ro} onChange={(e) => setBox(b.key, { inner_width: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.inner_length} disabled={ro} onChange={(e) => setBox(b.key, { inner_length: e.target.value })} /></td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-slate-600 whitespace-nowrap">{liters == null ? '—' : `${liters.toLocaleString('pt-BR')} L`}</td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.tare} disabled={ro} onChange={(e) => setBox(b.key, { tare: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.max_weight} disabled={ro} onChange={(e) => setBox(b.key, { max_weight: e.target.value })} /></td>
                        <td className="px-2 py-1.5 w-24"><Input value={b.priority} disabled={ro} onChange={(e) => setBox(b.key, { priority: e.target.value })} /></td>
                        <td className="px-2 py-1.5"><Check checked={b.enabled} disabled={ro} onChange={(v) => setBox(b.key, { enabled: v })} label="" /></td>
                        <td className="px-2 py-1.5 w-10">
                          {canWrite && (
                            <button type="button" className="text-slate-400 hover:text-red-600" title="Remover" onClick={() => update((p) => ({ ...p, boxes: p.boxes.filter((x) => x.key !== b.key) }))}>
                              <Trash2 size={15} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rótulos */}
      <Card>
        <CardHeader
          title="Rótulos das opções de frete"
          subtitle="Nomes que o cliente vê no site e no CRM. O hub acrescenta a cada rótulo o sufixo de prazo ({days} = dias úteis). Mudar um texto muda o que o cliente vê: rode a regressão depois."
          action={canWrite ? (
            <Button variant="outline" size="sm" icon={<Plus size={14} />} onClick={() => update((p) => ({
              ...p,
              service_labels: [...p.service_labels, { key: newRowKey--, provider_key: 'fmtransportes', role: 'cheapest', template: '' }],
            }))}>
              Novo rótulo
            </Button>
          ) : undefined}
        />
        <CardContent>
          {form.service_labels.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum rótulo cadastrado: o hub usa os nomes padrão do ProductsAPI.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-2 py-1.5">Transportadora</th>
                    <th className="px-2 py-1.5">Papel</th>
                    <th className="px-2 py-1.5">Texto</th>
                    <th className="px-2 py-1.5">Exemplo (6 dias)</th>
                    <th className="px-2 py-1.5" />
                  </tr>
                </thead>
                <tbody>
                  {form.service_labels.map((l) => (
                    <tr key={l.key} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 w-52">
                        <Select value={l.provider_key} disabled={ro} onChange={(e) => setLabel(l.key, { provider_key: e.target.value as FreightHubProviderKey | '*' })}>
                          <option value="*">{labelProviderName('*')}</option>
                          {FREIGHT_HUB_PROVIDER_KEYS.map((k) => <option key={k} value={k}>{PROVIDER_LABELS[k]}</option>)}
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 w-44">
                        <Select value={l.role} disabled={ro} onChange={(e) => setLabel(l.key, { role: e.target.value as FreightHubLabelRole })}>
                          {FREIGHT_HUB_LABEL_ROLES.map((r) => <option key={r} value={r}>{LABEL_ROLE_LABELS[r]}</option>)}
                        </Select>
                      </td>
                      <td className="px-2 py-1.5 min-w-[12rem]"><Input value={l.template} disabled={ro} onChange={(e) => setLabel(l.key, { template: e.target.value })} /></td>
                      <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">
                        {l.role === 'days_suffix' ? <span className="text-slate-400">—</span> : previewServiceLabel(form.service_labels, l.template, 6)}
                      </td>
                      <td className="px-2 py-1.5 w-10">
                        {canWrite && (
                          <button type="button" className="text-slate-400 hover:text-red-600" title="Remover" onClick={() => update((p) => ({ ...p, service_labels: p.service_labels.filter((x) => x.key !== l.key) }))}>
                            <Trash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acurácia */}
      <Card>
        <CardHeader
          title="Acurácia do empacotamento"
          subtitle="Caixas previstas pelo hub × caixas reais dos pedidos enviados, nas duas estratégias. Calculado todo dia às 04:30 pelo processo freight_hub_packing_learn."
          action={
            <Button variant="outline" size="sm" icon={<RefreshCw size={14} />} loading={reportLoading} onClick={loadReport}>
              Atualizar
            </Button>
          }
        />
        <CardContent>
          {reportError && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 mb-3">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {reportError}
            </div>
          )}
          {!report && reportLoading && <p className="text-sm text-slate-500">Carregando...</p>}
          {report && packingSummary.length === 0 && (
            <p className="text-sm text-slate-500">Sem comparações ainda. O processo roda diariamente e compara os pedidos enviados nos últimos 14 dias.</p>
          )}
          {packingSummary.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {packingSummary.map((sm) => (
                  <div key={sm.strategy} className={`rounded-lg border px-3 py-2 ${form.packing_strategy === sm.strategy ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        {STRATEGY_LABELS[sm.strategy] ?? sm.strategy}{form.packing_strategy === sm.strategy ? ' · em uso' : ''}
                      </span>
                      <span className="text-xs text-slate-500">{intBR(sm.orders)} pedidos</span>
                    </div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-800">{pctBR(sm.equal_pct)}</div>
                    <div className="text-xs text-slate-600">
                      com o nº certo de caixas · {intBR(sm.over)} acima · {intBR(sm.under)} abaixo
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 tabular-nums">
                      Caixas previstas {intBR(sm.predicted_boxes)} × reais {intBR(sm.actual_boxes)} ({sm.box_error_pct > 0 ? '+' : ''}{pctBR(sm.box_error_pct)})
                    </div>
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-1.5">Semana de</th>
                      {packingSummary.map((sm) => (
                        <th key={sm.strategy} className="px-2 py-1.5 text-right">{sm.strategy}: acerto · acima · abaixo · caixas</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {packingWeeks.map((w) => (
                      <tr key={w.week_start} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 whitespace-nowrap">{formatDateBR(w.week_start)}</td>
                        {packingSummary.map((sm) => {
                          const cell = w.byStrategy[sm.strategy];
                          return (
                            <td key={sm.strategy} className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                              {cell ? `${pctBR(cell.equal_pct)} · ${cell.over} · ${cell.under} · ${cell.predicted_boxes}/${cell.actual_boxes}` : '—'}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {report && (
            <div className="mt-5">
              <div className="text-xs font-medium text-slate-600 mb-2">
                Volume efetivo aprendido: {intBR(report.stats_sku_count)} SKUs
                {report.stats_at ? ` (calculado em ${formatDateTimeBR(report.stats_at)})` : ''}. Mais vendidos:
              </div>
              {(report.top_skus ?? []).length === 0 ? (
                <p className="text-sm text-slate-500">Nenhum SKU com estatística ainda (precisa de pedidos de 1 caixa e 1 SKU).</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-2 py-1.5">SKU</th>
                        <th className="px-2 py-1.5 text-right">Volume efetivo (cm³)</th>
                        <th className="px-2 py-1.5 text-right">Cadastro (cm³)</th>
                        <th className="px-2 py-1.5 text-right">Efetivo ÷ cadastro</th>
                        <th className="px-2 py-1.5 text-right">Amostras</th>
                        <th className="px-2 py-1.5">Origem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report.top_skus ?? []).map((st) => (
                        <tr key={st.sku} className="border-t border-slate-100">
                          <td className="px-2 py-1.5 font-medium text-slate-800">{st.sku}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{intBR(st.effective_volume_cm3)}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{st.gross_volume_cm3 ? intBR(st.gross_volume_cm3) : '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums">
                            {st.gross_volume_cm3 ? (st.effective_volume_cm3 / st.gross_volume_cm3).toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{intBR(st.samples)}</td>
                          <td className="px-2 py-1.5 text-slate-600">{st.method === 'manual' ? 'manual' : 'envios'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
